import { useCallback, useRef, useMemo, useContext, useEffect, useState } from 'react';
import { WebViewManagerContext } from 'app/home/general-components/WebViewManagerContext';
import { toIntDeviceTypeForSoloAddDevice, toStrDeviceType } from './Device';
import packageJson from '../../../package.json';

export default function useAddDeviceController(sendEvent, negotiatedProtocol, homeId) {
    const addDeviceCallbacks = useRef({});
    const registerNotification = useCallback((callbackId, callback) => {
        addDeviceCallbacks.current[callbackId] = callback;
    }, []);
    const unregisterNotification = useCallback((callbackId) => {
        if (callbackId in addDeviceCallbacks.current)
            delete addDeviceCallbacks.current[callbackId];
    }, []);

    const onAddDevice = useCallback((message) => {
        switch (message.msg.sub_event) {
            case "_webview-got-wifi-info":
            case "device_found_notification":
            case "error":
            case "search_has_started":
            case "search_has_ended":
            case "device_joined_notification":
                for (const id in addDeviceCallbacks.current) {
                    // if (addDeviceCallbacks.current.hasOwnProperty(id)) {
                    if (Object.prototype.hasOwnProperty.call(addDeviceCallbacks.current, id)) {
                        const callback = addDeviceCallbacks.current[id];
                        callback(message.msg);
                    }
                }
                break;
            default:
                break;
        }
    }, []);

    const standaloneAddDevice = useStandaloneAddDevice(sendEvent, negotiatedProtocol, onAddDevice, homeId);
    const standaloneAddDeviceIsActive = standaloneAddDevice.isActive;
    const standaloneAddDevicePrepare = standaloneAddDevice.prepare;

    const prepare = useCallback(() => {
        if (standaloneAddDeviceIsActive) {
            standaloneAddDevicePrepare();
        }
    }, [standaloneAddDeviceIsActive, standaloneAddDevicePrepare]);

    const search = useCallback((deviceTypes, ssid, password, bssid) => {
        let json = {
            "sub_event": "start_search",
            "device_types": deviceTypes
        }
        if (ssid !== undefined)
            json["ssid"] = ssid;
        if (password !== undefined)
            json["password"] = password;
        if (bssid !== undefined)
            json["bssid"] = ssid;

        if (standaloneAddDevice.isActive) {
            standaloneAddDevice.search(json)
        } else {
            sendEvent('add_device', json);
        }
    }, [sendEvent, standaloneAddDevice]);

    const join = useCallback((deviceId, roomIdToAddTo, config) => {
        let json;
        if (roomIdToAddTo !== undefined) {
            json = {
                "sub_event": "join_device",
                "device_id": deviceId,
                "room_id": roomIdToAddTo,
                "config": config
            };
        } else {
            json = {
                "sub_event": "join_device",
                "device_id": deviceId,
                "config": config
            };
        }
        if (standaloneAddDevice.isActive) {
            standaloneAddDevice.joinDevice(json)
        } else {
            sendEvent('add_device', json);
        }
    }, [sendEvent, standaloneAddDevice]);

    const indicate = useCallback((deviceId) => {
        if (standaloneAddDevice.isActive) {
            standaloneAddDevice.indicateDevice(deviceId)
        } else {
            sendEvent('indicate_device', {
                "device_id": deviceId
            });
        }
    }, [sendEvent, standaloneAddDevice]);

    const addDeviceController = useMemo(() => {
        return {
            registerNotification,
            unregisterNotification,

            prepare,
            search,
            indicate,
            join,
        }
    }, [registerNotification, unregisterNotification, prepare, search, join, indicate]);
    return [onAddDevice, addDeviceController, standaloneAddDevice.onSoloAddDevice];
}

const UDP_LISTEN_PORT = 3361;
const UDP_SEND_PORT = 3362;

const MSG_ID_SEARCH_FOR_SOLO_DEVICE = 1001;
const MSG_ID_JOIN_SOLO = 1002;

const SEARCH_DURATION = 90; // seconds
// const SEARCH_DURATION = 5; // seconds

function useStandaloneAddDevice(sendEvent, negotiatedProtocol, dispatchEventInWebapp, homeId) {
    const wm = useContext(WebViewManagerContext);

    const isActive = negotiatedProtocol.isSolo && (wm.webViewState.supportUDP && wm.webViewState.supportESPTouchV1);

    const [searchState, setSearchState] = useState(undefined);

    // udp sender (for search)
    useEffect(() => {
        if ((searchState !== undefined) && (searchState.endsAt > Date.now())) {
            const id = setTimeout(() => {
                setSearchState(s => {
                    if (s !== undefined) {
                        const ss = { ...s };
                        ss.lastUDP = Date.now();
                        if (ss.nounce) {
                            const json = {
                                "jsonrpc": "2.0",
                                "method": "search_for_solo_device",
                                "params": {
                                    "server_type": packageJson.serverName,
                                    "nounce": ss.nounce,
                                    "brand": "hinava",
                                    "types": ss.strDeviceTypes
                                },
                                "id": MSG_ID_SEARCH_FOR_SOLO_DEVICE
                            }
                            wm.notify("broadcast-udp", "auto", UDP_SEND_PORT, JSON.stringify(json));
                            // wm.notify("broadcast-udp", "255.255.255.255", UDP_SEND_PORT, JSON.stringify(json));
                        }
                        return ss;
                    }
                });
            }, Math.max(0, 1000 - (Date.now() - searchState.lastUDP)));
            return () => { clearTimeout(id) };
        }
        // else {
        //     // setSearchState(undefined);
        //     dispatchEventInWebapp({ "msg": { "sub_event": "search_has_ended" }, "ev": "add_device" });
        // }
    }, [searchState, wm, dispatchEventInWebapp]);

    const prepareFuncs = useRef({});

    // udp reciever + wifi info reciever
    const prepare = useCallback(() => {
        const udpReciever = function (address, port, data) {
            console.log("udpReciever:", address, port, data);
            if (port === UDP_LISTEN_PORT) {
                const json = JSON.parse(data);
                if (json.id === MSG_ID_SEARCH_FOR_SOLO_DEVICE) {
                    setSearchState(s => {
                        if (s !== undefined) {
                            const ss = { ...s };
                            try {
                                const intDeviceType = toIntDeviceTypeForSoloAddDevice(json.result.type);
                                ss.devices[json.result.serial_id] = {
                                    serial_id: json.result.serial_id,
                                    address: address,
                                    token: json.result.token,
                                    deviceType: intDeviceType,
                                    strDeviceType: json.result.type,
                                };
                                dispatchEventInWebapp({
                                    "msg": {
                                        "sub_event": "device_found_notification",
                                        "device_id": json.result.serial_id,
                                        "name": json.result.serial_id.substring(0, 8),
                                        "device_type": intDeviceType,
                                    }, "ev": "add_device"
                                })
                            } catch (e) {
                                console.error("udp reciever [search_for_solo_device]", e);
                            }
                            return ss;
                        }
                    });
                } else if (json.id === MSG_ID_JOIN_SOLO) {
                    if (json.result === "ok") {
                        setSearchState(s => {
                            if (s !== undefined) {
                                const ss = { ...s };
                                for (const serialId in ss.devices) {
                                    const d = ss.devices[serialId];
                                    if (d.address === address) {
                                        d.added = true;
                                        dispatchEventInWebapp({
                                            "msg": {
                                                "sub_event": "device_joined_notification",
                                                "device_id": serialId,
                                                "mac": d.mac
                                            }, "ev": "add_device"
                                        })
                                        return ss;
                                    }
                                }
                                return s;
                            }
                        });
                    }
                } else {
                    console.log("udpReciever: wrong id", json.id);
                }
            } else {
                console.log("udpReciever: wrong port", port);
            }
        };
        const wifiInfoReciever = function (bssid, ssid) {
            console.log("wifiInfoReciever:", bssid, ssid);
            dispatchEventInWebapp({
                "msg": {
                    "sub_event": "_webview-got-wifi-info",
                    "bssid": bssid,
                    "ssid": ssid,
                }, "ev": "add_device"
            });
        };

        wm.addListener("got-wifi-info", wifiInfoReciever);
        if (prepareFuncs.current.wifiInfoReciever)
            wm.removeListener("got-wifi-info", wifiInfoReciever);
        prepareFuncs.current.wifiInfoReciever = wifiInfoReciever;

        wm.addListener("got-udp", udpReciever);
        if (prepareFuncs.current.udpReciever)
            wm.removeListener("got-udp", udpReciever);
        prepareFuncs.current.udpReciever = udpReciever;

        wm.notify("listen-udp", UDP_LISTEN_PORT);
        wm.notify("get-wifi-info");
    }, [wm, dispatchEventInWebapp]);

    const search = useCallback(({ device_types, ssid, bssid, password }) => { // SEARCH_DURATION seconds, 1 udp each second starting immediately
        if ((searchState !== undefined) && (searchState.endsAt > Date.now())) {
            dispatchEventInWebapp({ "msg": { "sub_event": "error", "error": "another_search_in_progress" }, "ev": "add_device" });
            return;
        }
        dispatchEventInWebapp({ "msg": { "sub_event": "search_has_started", "search_duration": SEARCH_DURATION }, "ev": "add_device" });
        setTimeout(() => {
            // dispatchEventInWebapp uses a ref, so it's ok use it here after SEARCH_DURATION
            dispatchEventInWebapp({ "msg": { "sub_event": "search_has_ended" }, "ev": "add_device" });
        }, SEARCH_DURATION*1000);
        setSearchState({
            endsAt: Date.now() + SEARCH_DURATION*1000,
            lastUDP: Date.now(),
            strDeviceTypes: toStrDeviceType(device_types),
            devices: {},
        });
        wm.notify("start-esptouch-v1", bssid, ssid, password, 1, "broadcast");
        sendEvent('solo_add_device', { "sub_event": "get_nounce" });
    }, [dispatchEventInWebapp, searchState, sendEvent, wm]);

    const joinDevice = useCallback(({ device_id, config }) => {
        if (searchState !== undefined) {
            const json = {
                "sub_event": "join_device",
                "device_serial_id": device_id,
                "device_token": searchState.devices[device_id].token,
                "device_type": searchState.devices[device_id].deviceTypeInt,
            }
            if (config)
                json["config"] = config;
            sendEvent('solo_add_device', json);
        }
    }, [sendEvent, searchState]);

    const indicateDevice = useCallback((deviceId) => {
        if (searchState !== undefined) {
            const d = searchState.devices[deviceId];
            if (d !== undefined) {
                const json = {
                    "jsonrpc": "2.0",
                    "method": "indicate"
                }
                wm.notify("unicast-udp", d.address, UDP_SEND_PORT, JSON.stringify(json));
            }
        }
    }, [wm, searchState]);

    const onSoloAddDevice = useCallback((message) => {
        switch (message.msg.sub_event) {
            case "get_nounce":
                setSearchState(s => {
                    if (s !== undefined) {
                        const ss = { ...s };
                        ss.nounce = message.msg.nounce;
                        return ss;
                    }
                });
                break;
            case "join_device":
                setSearchState(s => {
                    if (s !== undefined) {
                        const ss = { ...s };
                        const d = ss.devices[message.msg.device_serial_id];
                        if (d !== undefined) {
                            d.mac = message.msg.mac;
                            return ss;
                        }
                        return s;
                    }
                });
                if (searchState !== undefined) {
                    const d = searchState.devices[message.msg.device_serial_id];
                    if (d !== undefined) {
                        const json = {
                            "jsonrpc": "2.0",
                            "method": "join_solo",
                            "params": {
                                "home_id": homeId,
                                "server_type": packageJson.serverName,
                            },
                            "id": MSG_ID_JOIN_SOLO
                        };
                        wm.notify("unicast-udp", d.address, UDP_SEND_PORT, JSON.stringify(json));
                    }
                }
                break;
            case "error":
                dispatchEventInWebapp({ "msg": { "sub_event": "error", "error": message.msg.error, "device_id": message.msg.device_serial_id }, "ev": "add_device" })
                break;
            default:
                break;
        }
    }, [dispatchEventInWebapp, searchState, wm, homeId]);

    return {
        isActive,
        prepare, search, joinDevice, indicateDevice,
        onSoloAddDevice,
    }
}

